import { useEffect, useState } from "react";
import { ShieldCheck, Search, Loader2, MoreVertical, MailCheck, FileUp, BarChart3, FolderOpen, X, Eye, ExternalLink, History, BellRing } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { NudgeChannelModal } from "@/components/NudgeChannelModal";

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={testid}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AdminVerifiedAgents() {
  const [agents, setAgents] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [menu, setMenu] = useState("");
  const [acting, setActing] = useState("");
  const [report, setReport] = useState(null);
  const [docs, setDocs] = useState(null);
  const [history, setHistory] = useState(null);
  const [modalAgent, setModalAgent] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [nudge, setNudge] = useState(null);
  const [nlog, setNlog] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [nudgeCtx, setNudgeCtx] = useState(null); // { action, agent|null, sellerIds }

  const load = () => {
    api.get(`/admin/agents${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setAgents(r.data.agents || [])).catch(() => setAgents([]));
    api.get("/admin/agents/nudge-config").then((r) => setNudge(r.data)).catch(() => {});
    api.get("/admin/agents/nudge-log?limit=100").then((r) => setNlog(r.data.log || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = agents && agents.length > 0 && sel.size === agents.length;
  const toggleAll = () => setSel(allSelected ? new Set() : new Set((agents || []).map((a) => a.seller_id)));

  const bulkAction = (action) => {
    if (sel.size === 0) return;
    setMenu("");
    setNudgeCtx({ action, agent: null, sellerIds: [...sel] });
  };

  const doNudge = async (channels) => {
    const { action, agent, sellerIds } = nudgeCtx;
    if (agent) {
      if (action === "send-verification") {
        const r = await api.post(`/admin/agents/${agent.seller_id}/send-verification`, { channels });
        toast.success(r.data.already_verified ? `Reminder sent — ${agent.name}'s mobile is already verified` : `Verification request sent to ${agent.name}`);
      } else {
        await api.post(`/admin/agents/${agent.seller_id}/request-documents`, { channels });
        toast.success(`Document request sent to ${agent.name}`);
      }
    } else {
      setBulkBusy(true);
      try {
        const r = await api.post("/admin/agents/bulk-action", { seller_ids: sellerIds, action, channels });
        toast.success(`Sent to ${r.data.sent} of ${r.data.total} agent(s)`);
        setSel(new Set());
      } finally { setBulkBusy(false); }
    }
    load();
  };

  const saveNudge = async (patch) => {
    const next = { enabled: nudge?.enabled || false, days: nudge?.days || 3, ...patch };
    setNudge((n) => ({ ...n, ...next }));
    try {
      const r = await api.put("/admin/agents/nudge-config", next);
      toast.success(r.data.enabled ? `Auto-Nudge ON · every ${r.data.days} day(s)` : "Auto-Nudge OFF");
      setNudge((n) => ({ ...n, ...r.data }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewHistory = async (a) => {
    setMenu(""); setActing(a.seller_id); setModalAgent(a); setHistory(null);
    try {
      const r = await api.get(`/admin/agents/${a.seller_id}/history`);
      setHistory(r.data.history || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setModalAgent(null); }
    finally { setActing(""); }
  };

  const toggle = async (a) => {
    setBusy(a.seller_id);
    try {
      const r = await api.post(`/admin/agents/${a.seller_id}/verified`, { verified: !a.verified_seller });
      setAgents((list) => list.map((x) => (x.seller_id === a.seller_id ? { ...x, verified_seller: r.data.verified_seller } : x)));
      toast.success(r.data.verified_seller ? "Marked as Verified Agent" : "Verified badge removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const sendVerification = (a) => { setMenu(""); setNudgeCtx({ action: "send-verification", agent: a, sellerIds: [a.seller_id] }); };

  const requestDocuments = (a) => { setMenu(""); setNudgeCtx({ action: "request-documents", agent: a, sellerIds: [a.seller_id] }); };

  const viewReport = async (a) => {
    setMenu(""); setActing(a.seller_id); setModalAgent(a); setReport(null);
    try {
      const r = await api.get(`/admin/agents/${a.seller_id}/report`);
      setReport(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setModalAgent(null); }
    finally { setActing(""); }
  };

  const viewDocuments = async (a) => {
    setMenu(""); setActing(a.seller_id); setModalAgent(a); setDocs(null);
    try {
      const r = await api.get(`/admin/agents/${a.seller_id}/documents`);
      setDocs(r.data.documents || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setModalAgent(null); }
    finally { setActing(""); }
  };

  const openFile = async (sellerId, d) => {
    try {
      const r = await api.get(`/admin/seller-applications/${sellerId}/documents/${d.document_id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not open file"); }
  };

  const closeModals = () => { setModalAgent(null); setReport(null); setDocs(null); setHistory(null); };
  const verifiedCount = (agents || []).filter((a) => a.verified_seller).length;

  const ACTIONS = [
    ["send-verification", "Send Verification Email", MailCheck, "Ask them to verify their mobile — choose Email / WhatsApp", sendVerification],
    ["request-documents", "Request Documents", FileUp, "Ask them to upload KYC / GST documents", requestDocuments],
    ["view-report", "View Report", BarChart3, "Recent traffic & customer inquiries", viewReport],
    ["view-documents", "View Documents", FolderOpen, "Files they've already uploaded", viewDocuments],
    ["view-history", "View History", History, "Timeline of requests sent to this agent", viewHistory],
  ];

  return (
    <div data-testid="admin-verified-agents">
      <PageHeader title="Verified Agents" icon={ShieldCheck} subtitle="Designate agents as Verified and run one-click actions — request mobile verification, ask for KYC/GST documents, and review their traffic & documents." />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} data-testid="va-search" placeholder="Search agent name or email…" className="h-9 w-64 rounded-full border border-slate-200 pl-8 pr-3 text-sm outline-none focus:border-emerald-400" />
        </div>
        <button onClick={load} data-testid="va-search-btn" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Search</button>
        {agents && <span className="ml-auto text-xs font-semibold text-emerald-700" data-testid="va-count">{verifiedCount} verified · {agents.length} total</span>}
      </div>

      {/* Auto-Nudge config */}
      {nudge && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3" data-testid="nudge-config">
          <BellRing className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-semibold text-slate-700">Auto-Nudge</span>
          <span className="text-xs text-slate-400">Re-ping agents who haven't verified mobile or uploaded docs</span>
          <div className="ml-auto flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-slate-500">every
              <input type="number" min="1" max="30" value={nudge.days || 3} onChange={(e) => setNudge((n) => ({ ...n, days: Number(e.target.value) }))} onBlur={(e) => saveNudge({ days: Number(e.target.value) })} data-testid="nudge-days" className="h-8 w-14 rounded-md border border-slate-200 px-2 text-center text-xs" /> days</label>
            {nudge.last_run_at && <span className="text-[11px] text-slate-400">last run {String(nudge.last_run_at).slice(0, 10)} · {nudge.last_nudged || 0} nudged</span>}
            <button onClick={() => saveNudge({ enabled: !nudge.enabled })} data-testid="nudge-toggle" className={`rounded-full px-3 py-1.5 text-xs font-semibold ${nudge.enabled ? "bg-emerald-500 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{nudge.enabled ? "On" : "Off"}</button>
          </div>
        </div>
      )}

      {/* Nudge Delivery Log */}
      {nlog !== null && (
        <div className="mb-3 rounded-2xl border border-slate-200 bg-white p-3" data-testid="nudge-log">
          <button onClick={() => setShowLog((v) => !v)} data-testid="nudge-log-toggle" className="flex w-full items-center gap-2 text-left">
            <MailCheck className="h-4 w-4 text-sky-500" />
            <span className="text-sm font-semibold text-slate-700">Nudge Delivery Log</span>
            <span className="text-xs text-slate-400">{nlog.length} recent · channel used &amp; whether it landed or was skipped</span>
            <span className="ml-auto text-xs font-semibold text-sky-600">{showLog ? "Hide" : "Show"}</span>
          </button>
          {showLog && (nlog.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-400" data-testid="nudge-log-empty">No nudges sent yet. Use the per-agent or bulk actions above to send one.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[640px] text-xs">
                <thead className="text-left uppercase text-slate-400"><tr><th className="p-2">When</th><th className="p-2">Agent</th><th className="p-2">Nudge</th><th className="p-2">Email</th><th className="p-2">WhatsApp</th><th className="p-2">In-app</th><th className="p-2">By</th></tr></thead>
                <tbody>
                  {nlog.map((n, i) => {
                    const chip = (v) => v === "sent" ? "bg-emerald-50 text-emerald-700" : v === "skipped" ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-400";
                    const lbl = (v) => v === "sent" ? "landed" : v === "skipped" ? "skipped" : v === "not_selected" ? "—" : (v || "—");
                    const d = n.delivery || {};
                    return (
                      <tr key={i} className="border-t border-slate-100" data-testid={`nudge-log-row-${i}`}>
                        <td className="p-2 text-slate-500">{String(n.at || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="p-2 text-slate-700">{n.seller_name || "—"}<br /><span className="text-slate-400">{n.seller_email}</span></td>
                        <td className="p-2 text-slate-600">{n.action_label || n.action}</td>
                        <td className="p-2"><span className={`rounded-full px-2 py-0.5 font-semibold ${chip(d.email)}`} data-testid={`nudge-log-email-${i}`}>{lbl(d.email)}</span></td>
                        <td className="p-2"><span className={`rounded-full px-2 py-0.5 font-semibold ${chip(d.whatsapp)}`} data-testid={`nudge-log-wa-${i}`}>{d.whatsapp === "skipped" && !n.has_phone ? "no phone" : lbl(d.whatsapp)}</span></td>
                        <td className="p-2"><span className={`rounded-full px-2 py-0.5 font-semibold ${chip(d.in_app)}`}>{lbl(d.in_app)}</span></td>
                        <td className="p-2 text-slate-400">{n.auto ? "auto-nudge" : (n.actor || "—")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Bulk toolbar */}
      {agents && agents.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 px-3 py-2" data-testid="bulk-toolbar">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="bulk-select-all" className="h-4 w-4 rounded border-slate-300" /> Select all</label>
          <span className="text-xs text-slate-400" data-testid="bulk-count">{sel.size} selected</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => bulkAction("send-verification")} disabled={bulkBusy || sel.size === 0} data-testid="bulk-send-verification" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"><MailCheck className="h-3.5 w-3.5" /> Send verification</button>
            <button onClick={() => bulkAction("request-documents")} disabled={bulkBusy || sel.size === 0} data-testid="bulk-request-documents" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"><FileUp className="h-3.5 w-3.5" /> Request documents</button>
          </div>
        </div>
      )}

      {agents === null ? (
        <div className="flex h-48 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : agents.length === 0 ? (
        <p className="rounded-2xl border border-slate-100 p-8 text-center text-sm text-slate-400">No agents found.</p>
      ) : (
        <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
          {agents.map((a) => (
            <div key={a.seller_id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`va-row-${a.seller_id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <input type="checkbox" checked={sel.has(a.seller_id)} onChange={() => toggleSel(a.seller_id)} data-testid={`va-select-${a.seller_id}`} className="h-4 w-4 shrink-0 rounded border-slate-300" />
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-semibold text-slate-800">{a.name || "Agent"}
                    {a.verified_seller && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><ShieldCheck className="h-3 w-3" /> Verified</span>}
                  </p>
                  <p className="text-xs text-slate-400">{a.email}{a.application_status ? ` · application: ${a.application_status}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggle(a)} disabled={busy === a.seller_id} data-testid={`va-toggle-${a.seller_id}`} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-50 ${a.verified_seller ? "border border-slate-200 text-slate-600 hover:bg-slate-50" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>
                  {busy === a.seller_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                  {a.verified_seller ? "Remove verification" : "Mark as Verified"}
                </button>
                <div className="relative">
                  <button onClick={() => setMenu(menu === a.seller_id ? "" : a.seller_id)} disabled={acting === a.seller_id} data-testid={`va-actions-${a.seller_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                    {acting === a.seller_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MoreVertical className="h-3.5 w-3.5" />} Actions
                  </button>
                  {menu === a.seller_id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setMenu("")} />
                      <div className="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg" data-testid={`va-actions-menu-${a.seller_id}`}>
                        {ACTIONS.map(([key, label, Icon, hint, fn]) => (
                          <button key={key} onClick={() => fn(a)} data-testid={`va-action-${key}-${a.seller_id}`} className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50">
                            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                            <span><span className="block text-sm font-semibold text-slate-700">{label}</span><span className="block text-[11px] text-slate-400">{hint}</span></span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Report modal */}
      {modalAgent && report !== null && (
        <Modal title={`Report · ${modalAgent.name || "Agent"}`} onClose={closeModals} testid="va-report-modal">
          {!report ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> : (
            <div className="space-y-4">
              <div>
                <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><BarChart3 className="h-4 w-4 text-emerald-500" /> Storefront & search traffic</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[["Views", report.traffic.views, report.traffic.views_30d], ["Searches", report.traffic.searches, report.traffic.searches_30d], ["Product clicks", report.traffic.product_clicks, report.traffic.clicks_30d], ["QR scans", report.traffic.qr_scans, null]].map(([l, v, r30]) => (
                    <div key={l} className="rounded-lg bg-slate-50 p-3"><p className="text-[10px] uppercase tracking-wide text-slate-400">{l}</p><p className="text-xl font-bold text-slate-800">{v}</p>{r30 != null && <p className="text-[10px] text-emerald-600">{r30} in 30d</p>}</div>
                  ))}
                </div>
              </div>
              {report.top_products?.length > 0 && (
                <div>
                  <p className="mb-1 text-sm font-semibold text-slate-700">Top clicked products</p>
                  {report.top_products.map((p) => <div key={p.product_id} className="flex justify-between border-b border-slate-50 py-1 text-sm"><span className="truncate text-slate-600">{p.name}</span><span className="font-semibold text-slate-500">{p.clicks} clicks</span></div>)}
                </div>
              )}
              <div>
                <p className="mb-2 text-sm font-semibold text-slate-700">Customer inquiries <span className="ml-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-600">{report.inquiry_totals.total} total · {report.inquiry_totals.service_bookings} bookings · {report.inquiry_totals.orders} orders</span></p>
                {report.inquiries?.length ? (
                  <div className="overflow-hidden rounded-lg border border-slate-100">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-400"><tr><th className="p-2">Type</th><th className="p-2">Ref</th><th className="p-2">Detail</th><th className="p-2">Status</th><th className="p-2">When</th></tr></thead>
                      <tbody className="divide-y divide-slate-50">
                        {report.inquiries.map((it, i) => (
                          <tr key={i} data-testid={`va-inquiry-${i}`}><td className="p-2 font-medium text-slate-700">{it.type}</td><td className="p-2 text-slate-500">{it.ref}</td><td className="p-2 text-slate-500">{it.title}</td><td className="p-2"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{it.status || "—"}</span></td><td className="p-2 text-slate-400">{(it.at || "").slice(0, 10)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-sm text-slate-400">No inquiries yet.</p>}
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Documents modal */}
      {modalAgent && docs !== null && (
        <Modal title={`Documents · ${modalAgent.name || "Agent"}`} onClose={closeModals} testid="va-documents-modal">
          {docs.length === 0 ? (
            <p className="rounded-xl border border-slate-100 p-6 text-center text-sm text-slate-400">This agent hasn't uploaded any documents yet. Use “Request Documents” to ask for their KYC/GST files.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {docs.map((d) => (
                <div key={d.document_id} className="flex items-center justify-between gap-3 py-3" data-testid={`va-doc-${d.document_id}`}>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-700">{d.name || d.code}</p>
                    <p className="text-xs text-slate-400">{d.filename || "—"} · <span className="capitalize">{(d.status || "uploaded").replace(/_/g, " ")}</span></p>
                  </div>
                  {d.file_available === false ? (
                    <span className="text-xs text-rose-500">Blocked by scan</span>
                  ) : (
                    <button onClick={() => openFile(modalAgent.seller_id, d)} data-testid={`va-doc-view-${d.document_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Eye className="h-3.5 w-3.5" /> View file <ExternalLink className="h-3 w-3" /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* History modal */}
      {modalAgent && history !== null && (
        <Modal title={`History · ${modalAgent.name || "Agent"}`} onClose={closeModals} testid="va-history-modal">
          {history.length === 0 ? (
            <p className="rounded-xl border border-slate-100 p-6 text-center text-sm text-slate-400">No actions recorded yet for this agent.</p>
          ) : (
            <ol className="relative ml-2 border-l border-slate-200">
              {history.map((h, i) => (
                <li key={i} className="mb-4 ml-4" data-testid={`va-history-${i}`}>
                  <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-emerald-400" />
                  <p className="text-sm font-semibold text-slate-700">{(h.action || "").replace(/[._]/g, " ")}</p>
                  <p className="text-[11px] text-slate-400">{(h.at || "").replace("T", " ").slice(0, 16)} · by {h.by || h.by_role || "system"}{h.after ? ` · → ${h.after}` : ""}{h.meta?.reasons ? ` · ${h.meta.reasons.join(", ")}` : ""}</p>
                </li>
              ))}
            </ol>
          )}
        </Modal>
      )}

      {/* Nudge channel picker + preview */}
      {nudgeCtx && (
        <NudgeChannelModal
          open={!!nudgeCtx}
          action={nudgeCtx.action}
          sellerId={nudgeCtx.agent ? nudgeCtx.agent.seller_id : null}
          sellerName={nudgeCtx.agent ? nudgeCtx.agent.name : ""}
          count={nudgeCtx.agent ? 1 : nudgeCtx.sellerIds.length}
          onSend={doNudge}
          onClose={() => setNudgeCtx(null)}
          testid="va-nudge-modal"
        />
      )}
    </div>
  );
}
